import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "types";

import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";

import { db } from "config/firebase";
import { doc, setDoc } from "firebase/firestore";

import TextEditor from "app/Components/Fields/TextEditor";

interface SendPMProps {
  receiverName: string;
  receiverId: string;
  firstMsg?: boolean;
}

function SendPM(props: SendPMProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const [message, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>("");

  const sendMessage = async () => {
    if (!user?.uid || !message.trim()) return;

    setSending(true);
    setError("");

    try {
      const msgId = user.uid + Date.now();
      const time = Date.now();
      const msg = {
        id: msgId,
        content: message,
        senderId: user.uid,
        senderName: user.displayName || "Anonymous",
        read: false,
        receiverName: props.receiverName,
        receiverId: props.receiverId,
        timeStamp: time,
      };

      await setDoc(doc(db, "messages", msgId), msg);
      setMsg("");
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && event.ctrlKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Send a message to {props.receiverName}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextEditor
          text={message}
          handleChange={setMsg}
          placeholder="Type your message here... (Ctrl+Enter to send)"
          backgroundColor="white"
          onKeyDown={handleKeyPress}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          width: "100%",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={sendMessage}
          disabled={sending || !message.trim()}
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          sx={{ minWidth: 120 }}
        >
          {sending ? "Sending..." : "Send Message"}
        </Button>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>

    </Box>
  );
}

export default SendPM;
